import { isDefined } from '~/utils/guards/isDefined';
import { useHostname } from '~/composables/useHostname';
import { useContentfulImageCdnPreconnect } from '~/composables/useContentfulImageCdnPreconnect';

export function useGlobalMetadata() {
  useContentfulImageCdnPreconnect();
  const slug = useSlug();
  const { $globalPageSettings, $activeLocale } = useNuxtApp();

  // XXX: access to useRuntimeConfig inside buildUrlString results
  // in a "can not use useNuxtApp" error...
  const serverUrlOrigin = useHostname();
  const url = computed(() =>
    buildUrlString(
      $activeLocale.value.webapp,
      slug.value,
      undefined,
      undefined,
      serverUrlOrigin
    )
  );

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: $globalPageSettings.value?.companyName ?? '',
          url: url.value,
          logo: $globalPageSettings.value?.companyLogo?.url ?? undefined,
          address: {
            '@type': 'PostalAddress',
            streetAddress:
              $globalPageSettings.value?.companyStreet ?? undefined,
            postalCode:
              $globalPageSettings.value?.companyPostalCode ?? undefined,
            addressLocality:
              $globalPageSettings.value?.companyCity ?? undefined,
            addressRegion:
              $globalPageSettings.value?.companyRegion ?? undefined,
            addressCountry:
              $activeLocale.value.webapp?.split('-').at(-1)?.toUpperCase() ??
              undefined
          },
          sameAs: (
            $globalPageSettings.value?.companySocialMediaLinks?.items ?? []
          )
            .map((link) => {
              const url = link.link.url;
              if (
                link.link.external ||
                url.startsWith('http://') ||
                url.startsWith('https://')
              ) {
                return url;
              }

              return undefined;
            })
            .filter(isDefined)
        }),
        tagPriority: -1 // Wird als letztes eingefügt
      }
    ]
  });

  // the lang attribute should follow RFC 5646
  // @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/lang
  const htmlLang = computed(() => {
    const [lang, country] = $activeLocale.value.webapp?.split('-') ?? [];
    if (!lang) {
      return;
    }
    if (!country) {
      return lang.toLowerCase();
    }

    return `${lang.toLowerCase()}-${country.toUpperCase()}`;
  });

  // every page should have a lang attribute. this might be overwritten
  // by "useMetadata" with a local from a page/metadata.
  useHead({
    htmlAttrs: {
      lang: () => htmlLang.value
    }
  });
}
